import 'bootstrap/dist/css/bootstrap.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import App from './App';
import { correcttrackStore, persistor } from './store/correcttrackStore';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import authService, { injectStoreForAuth } from './auth/AuthService';
import { injectStore } from './api/CustomAxios';
import i18n, { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './i18n';
import { setLanguage } from './store/correcttrackSlice';
import { correctTrackTheme } from './theme';

injectStoreForAuth(correcttrackStore);
injectStore(correcttrackStore);

// Resolve initial language: persisted Redux value wins; otherwise fall back to
// what i18n detected from navigator.language (filtered to supported locales).
const persistedLanguage = correcttrackStore.getState().correcttrack?.language;
if (persistedLanguage && SUPPORTED_LOCALES.includes(persistedLanguage)) {
    i18n.changeLanguage(persistedLanguage);
} else {
    correcttrackStore.dispatch(setLanguage(i18n.language || DEFAULT_LOCALE));
}

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

const app = (
    <BrowserRouter>
        <Provider store={correcttrackStore}>
            {/* Rehydrating from localStorage is async. PersistGate holds the first render until
                it lands, so components that seed local state from the store on mount -- the
                Tasks and Hazards grids read their saved filters and column visibility exactly
                once -- see the restored values rather than the empty defaults. */}
            <PersistGate loading={null} persistor={persistor}>
                <ThemeProvider theme={correctTrackTheme}>
                    <App />
                </ThemeProvider>
            </PersistGate>
        </Provider>
    </BrowserRouter>);

// The access token is no longer persisted, so on a reload the store starts with none and every
// route guard would read the user as signed out. Trade the httpOnly refresh cookie for a fresh
// access token first, then render. Costs one round trip on a cold load; a signed-out visitor
// gets a rejected refresh and falls through to the login page just as before.
authService.restoreSession().finally(() => root.render(app));
